import { computed, reactive, set } from '@nuxtjs/composition-api';
import { StateInterface, UseProductPageInterface, ModuleWithRequiredFlag } from '~/composables/useProductPage/useProductPage';
import { SKU_SEPARATOR } from '~/utils/constants/code';
import { BUNDLE_TYPE_ID, DURATION_MATCHING, DURATION_SPAN_MONTHLY } from '~/utils/constants/product';
import { GenericObjectType } from '~/utils/types';
import type {ProductForProductPageType} from '~/utils/types/ProductPage';
const state = reactive<StateInterface>({
  product: null,
  paymentFrequencyChoosen: null,
  addToCartConfigs: null,
  offset: 0,
  choosenModules: new Map(),
  modulesKeys: [], // We need this key map because the state can't watch complex objects as Map, using the keys as position-sku to make the sorting faster
  acceptedQuantities: 0,
  finalPrice: null,
  bundleTipoConsegna: null
});

/**
 * Global store for managing UI state.
 *
 * See the {@link UseUiStateInterface} for a list of methods and values available in this composable.
 */
export function useProductPage(): UseProductPageInterface {
  const setPaymentFrequencyChoosen = (val: string) => { state.paymentFrequencyChoosen = val; }
  const getLowPaymentFrequency = ():string => {
    try {
      return state.product['paymentFrequencies'][0]
    } catch (e) {
      console.log(`Error in useProductPage > getLowPaymentFrequency, returning M as default: ${e}`)
      return DURATION_SPAN_MONTHLY;
    }
  }
  const getHighPaymentFrequency = ():string => {
    try {
      return state.product.paymentFrequencies[1] ?? getLowPaymentFrequency();
    } catch (e) {
      console.log(`Error in useProductPage > getHighPaymentFrequency, returning low frequency as default: ${e}`)
      return getLowPaymentFrequency()
    }
  }
  
  const setProduct = (product: ProductForProductPageType) => {
    state.product = product;
    setAcceptedQuantities(product?.modulesInCartQty?.min),
    setPaymentFrequencyChoosen(getHighPaymentFrequency())
  }

  const setAcceptedQuantities = (qty: number) => { state.acceptedQuantities = qty; }
  
    const addModule = (module: ModuleWithRequiredFlag) => {
    const moduleKey = `${state.modulesKeys.length}${SKU_SEPARATOR}${module.sku}`;
    
    if (state.choosenModules.has(moduleKey)) {
      return false;
    }

    state.choosenModules.set(moduleKey, module);
    state.modulesKeys.push(moduleKey);
    
    return true;
  };

    const removeModule = (val: ModuleWithRequiredFlag) => {
    if (val?.isRequired) {
      return;
    }
    
    const moduleKeyToRemove = state.modulesKeys.find(key => key.includes(val.sku));
    if (moduleKeyToRemove) {
      const index = state.modulesKeys.indexOf(moduleKeyToRemove);
      state.modulesKeys.splice(index, 1);
      // @ts-ignore @TODO check! @Roberto Portaluri
      state.choosenModules.delete(moduleKeyToRemove);
    }
  }
  
  const toggleModule = (val: ModuleWithRequiredFlag) => {
    if(state.modulesKeys.length<state.acceptedQuantities || state.modulesKeys.includes(`${val.position}${SKU_SEPARATOR}${val.sku}`)){
      state.choosenModules.get(val.sku) ? removeModule(val) : addModule(val);
    }else{
      let moduleToRemove = null;
      
      for (let i = state.modulesKeys.length - 1; i >= 0; i--) {
        const index = state.modulesKeys[i];
        // @ts-ignore @TODO check! @Roberto Portaluri
        const module = state.choosenModules.get(index);
        
        if (module && !module.isRequired) {
          moduleToRemove = module;
          break;
        }
      }
      
      if (moduleToRemove) {
        removeModule(moduleToRemove);
        addModule(val);
      } 
    }
  }


  const checkOptionsMatching = (product, variant)=>{
    if(!state.addToCartConfigs) return true;

    return Object.keys(state.addToCartConfigs).every(option_uid => {
         const option = product.configurable_options.find(el=>el.uid==option_uid)
         if(!option) return true; //option not mapped like tipo_consegna
         const value = option.values.find(opt=>opt.uid==state.addToCartConfigs[option_uid]);
         return variant.product[option.attribute_code] == value.value_index;
     } )
 }

 /**
  * Don't check tipo_abbonamento because it's always pre-compiled (high payment frequency as default )
  */
 const checkForceDisableAddToCart = (product)=> {
     const disabled = /* page not ready */!product || (product.optionsToSelect && Object.values(product.optionsToSelect).length > 0 && !state.addToCartConfigs)
         /* there are modules to select */ || (product.modules?.length>0 && product.modulesInCartQty?.min>0 && state.modulesKeys.length < product.modulesInCartQty?.min)
         /* there are options to select */ || (product.optionsToSelect && product.optionsToSelect.length>0 && !product.optionsToSelect.every(opt=> Object.keys(state.addToCartConfigs).includes(opt.attribute_uid)))
     
     /** If not disabled set the product cost to show if the product price isn't set */
        const productPrice = product.prices[state.paymentFrequencyChoosen];

         if(!disabled && (productPrice !== undefined && productPrice !== null) && product.variants){
             /** find the right variant */
             const variant = product.variants.find(el=> 
             DURATION_MATCHING[el.product.tipo_abbonamento] == state.paymentFrequencyChoosen
             && checkOptionsMatching(product,el)
         )
             /** TMP fix - find the right way */
             state.finalPrice = ({price : variant.product.full_price ? variant.product.full_price :  variant.product.price_range.maximum_price.final_price.value, variant} )
         }
         return disabled;
     }

  return {
    product: computed(() => state.product),
    setProduct,
    setPaymentFrequencyChoosen,
    paymentFrequencyChoosen: computed(() => state.paymentFrequencyChoosen),
    productLowPaymentFrequency : computed(()=>getLowPaymentFrequency()),
    productHighPaymentFrequency: computed(() => getHighPaymentFrequency()),
    showPaymentFrequencySwitcher: computed(() => getLowPaymentFrequency() != getHighPaymentFrequency()), //@TODO remove
    addToCartConfigs: computed(() => state.addToCartConfigs),
    setAddToCartConfigs: (c: GenericObjectType) => state.addToCartConfigs = c,
    offset: computed(() => state.offset),
    setOffset: (val: number) => state.offset = val,
    addModule,
    removeModule,
    toggleModule,
    getChoosenModules: () => state.choosenModules,
    setAcceptedQuantities,
    modulesKeys: computed(() => state.modulesKeys as Array<String>),
    finalPrice: computed(() => state.finalPrice),
    setFinalPrice: (val:{price: number, variant:GenericObjectType})=>state.finalPrice = val,
    bundleTipoConsegna: computed(() => state.bundleTipoConsegna),
    setBundleTipoConsegna: (val?: string) => state.bundleTipoConsegna = val?.toLowerCase(),
    checkForceDisableAddToCart : (product: ProductForProductPageType)=>checkForceDisableAddToCart(product)
  };
}

export default useProductPage;
export * from './useProductPage';